import { useState } from 'react'
import { ChevronDown, ChevronRight, Mail, Phone, MapPin } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { RiskScoreBar } from '@/components/leads/RiskScoreBar'
import { PatternIndicators } from '@/components/leads/PatternIndicators'
import { LeadStatusBadge } from '@/components/leads/LeadStatusBadge'
import { useSwipeActions } from '@/hooks/useSwipeActions'
import { cn } from '@/lib/utils'
import type { LeadTableRow } from '@/services/mockLeads'

interface SwipeableLeadCardProps {
  lead: LeadTableRow
  onApprove: (leadId: string) => void
  onReject: (leadId: string) => void
  onViewDetails: (leadId: string) => void
}

export function SwipeableLeadCard({ 
  lead, 
  onApprove, 
  onReject, 
  onViewDetails 
}: SwipeableLeadCardProps) {
  const [expanded, setExpanded] = useState(false)
  const [showActions, setShowActions] = useState(false)

  const { bind, isSwiping, swipeProgress } = useSwipeActions({
    threshold: 100,
    onSwipeLeft: () => onReject(lead.id),
    onSwipeRight: () => onApprove(lead.id)
  })

  const swipeOffset = Math.max(-150, Math.min(150, swipeProgress.x))
  const showLeftAction = swipeOffset > 50
  const showRightAction = swipeOffset < -50

  const formatTimestamp = (date: Date) => {
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / 60000)
    
    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (minutes < 1440) return `${Math.floor(minutes / 60)}h ago`
    return date.toLocaleDateString()
  }

  return (
    <div className="relative overflow-hidden">
      {/* Swipe Action Backgrounds */}
      <div className="absolute inset-0 flex">
        {/* Approve (Right Swipe) */}
        <div className={cn(
          "flex-1 bg-green-500 flex items-center justify-start pl-6 transition-opacity",
          showLeftAction ? "opacity-100" : "opacity-0"
        )}>
          <div className="flex items-center gap-2 text-white font-medium">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              ✓
            </div>
            <span>Approve</span>
          </div>
        </div>

        {/* Reject (Left Swipe) */}
        <div className={cn(
          "flex-1 bg-red-500 flex items-center justify-end pr-6 transition-opacity",
          showRightAction ? "opacity-100" : "opacity-0"
        )}>
          <div className="flex items-center gap-2 text-white font-medium">
            <span>Reject</span>
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              ✗
            </div>
          </div>
        </div>
      </div>

      {/* Lead Card */}
      <Card 
        className={cn(
          "transition-transform duration-200 bg-background",
          isSwiping && "shadow-lg"
        )}
        style={{ 
          transform: isSwiping ? `translateX(${swipeOffset}px)` : 'translateX(0)' 
        }}
        {...bind}
      >
        <CardContent className="p-4">
          {/* Header Row */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <RiskScoreBar score={lead.riskScore} size="sm" className="w-16" />
              <div>
                <h3 className="font-medium text-sm">
                  {lead.firstName} {lead.lastName}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {formatTimestamp(lead.timestamp)}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <LeadStatusBadge status={lead.status} size="sm" />
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Contact Info */}
          <div className="space-y-2 mb-3">
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-3 w-3 text-muted-foreground" />
              <span className="truncate">{lead.email}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Phone className="h-3 w-3 text-muted-foreground" />
              <span>{lead.phone}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="h-3 w-3 text-muted-foreground" />
              <span>{lead.zip}</span>
              <Badge variant="outline" className="text-xs">
                {lead.ipType}
              </Badge>
            </div>
          </div>

          {/* Pattern Tags */}
          <div className="mb-3">
            <PatternIndicators patterns={lead.patterns} size="sm" />
          </div>

          {/* Expanded Details */}
          {expanded && (
            <div className="pt-3 border-t space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground">IP Type</p>
                  <Badge variant="outline" className="text-xs">
                    {lead.ipType}
                  </Badge>
                </div>
                <div>
                  <p className="text-muted-foreground">Source</p>
                  <Badge variant="secondary" className="capitalize text-xs">
                    {lead.source}
                  </Badge>
                </div>
              </div>
              
              {lead.notes && (
                <div>
                  <p className="text-muted-foreground text-sm">Notes</p>
                  <p className="text-sm bg-muted/30 p-2 rounded">{lead.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Swipe Hint */}
          {!isSwiping && !showActions && (
            <div className="text-center mt-3 pt-2 border-t">
              <p className="text-xs text-muted-foreground">
                ← Swipe left to reject • Swipe right to approve →
              </p>
            </div>
          )}

          {/* Touch Action Buttons */}
          <div className={cn(
            "flex gap-2 mt-3 pt-3 border-t transition-all duration-200",
            showActions ? "opacity-100 max-h-20" : "opacity-0 max-h-0 overflow-hidden"
          )}>
            <Button 
              onClick={() => onApprove(lead.id)} 
              variant="default" 
              size="sm" 
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              Approve
            </Button>
            <Button 
              onClick={() => onReject(lead.id)} 
              variant="destructive" 
              size="sm" 
              className="flex-1"
            >
              Reject
            </Button>
            <Button 
              onClick={() => onViewDetails(lead.id)} 
              variant="outline" 
              size="sm"
            >
              Details
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}